import React, { useEffect, useState } from 'react';
import { Box, Dialog, Divider, Fab, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { CancelPresentationRounded, ChevronLeft, ChevronRight, KeyboardArrowUp } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './styles';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import columns from './table_config';
import CanvasJSReact from '../../../../ui/canvasjs/canvasjs.react';
import { HYDRAULICS_CHART_COLORS } from '../../../../../shared/config/hydraulics_constants';
import { getOptimalInterval } from '../../../../../utils/getOptimalInterval';
import { renderCell } from '../../../../assetData/table_config';
import Datagrid from '../../../../ui/Datagrid';
import { isMobile } from 'react-device-detect';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function RunDetailsChartDialog({ onCloseClick, isOpen, data, hydraulicsLanding, units, featuredWell, window }) {
  const classes = styles();
  const chartData = [...data.row.depth_points];
  const [selectionMenu, setSelectionMenu] = useState([]);
  const [charttable, setCharttable] = useState('');
  const [tableRecord, setTableRecord] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [runDetails, setRundetails] = useState([]);
  const [showMobileChartTable, setShowMobileChartTable] = useState(false);
  const depthPoints = units?.parametricAnalysis?.results?.depth_points;

  const [chartConfig, setChartConfig] = useState([
    {
      name: 'ESD',
      key: 'esd',
      color: HYDRAULICS_CHART_COLORS.ESD,
      hide: false,
      tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ESD: {x} ${depthPoints?.esd?.label}`,
      columnFields: [{
        field: 'esd',
        headerName: `ESD (${depthPoints?.esd?.label})`,
        width: 120,
        headerAlign: 'center',
        align: 'center',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const value = Number(params.value);
          return isNaN(value) ? params.value : value.toFixed(depthPoints?.esd?.decimals);
        },
      }]
    },
    {
      name: 'ESD with SBP',
      key: 'esd_with_sbp',
      color: HYDRAULICS_CHART_COLORS.ESD_WITH_SBP,
      hide: false,
      tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ESD: {x} ${depthPoints?.esd_with_sbp?.label}`,
      columnFields: [
        {
          field: 'esd_with_sbp',
          headerName: `ESD (${depthPoints?.esd_with_sbp?.label})`,
          width: 120,
          headerAlign: 'center',
          align: 'center',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return '';
            }
            const value = Number(params.value);
            return isNaN(value) ? params.value : value.toFixed(depthPoints?.esd_with_sbp.decimals);
          },
        }]
    },
    {
      name: 'ECD',
      key: 'ecd',
      color: HYDRAULICS_CHART_COLORS.ECD,
      hide: false,
      tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ECD: {x} ${depthPoints?.ecd?.label} <br/> Annular Pressure: {ann_p} ${depthPoints?.ann_p?.label}`,
      columnFields: [
        {
          field: 'ecd',
          headerName: `ECD (${depthPoints?.ecd?.label})`,
          width: 120,
          headerAlign: 'center',
          align: 'center',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return '';
            }
            const value = Number(params.value);
            return isNaN(value) ? params.value : value.toFixed(depthPoints?.ecd?.decimals);
          },
        },
        {
          field: 'ann_p',
          headerName: `Annular Pressure (${depthPoints?.ann_p?.label})`,
          width: 160,
          headerAlign: 'center',
          align: 'center',
        },
      ],
    },
    {
      name: 'ECD with SBP',
      key: 'ecd_with_sbp',
      color: HYDRAULICS_CHART_COLORS.ECD_WITH_SBP,
      hide: false,
      tooltip: `MD: {y} ${depthPoints?.md?.label} <br/> ECD: {x} ${depthPoints?.ecd?.label} <br/> Annular Pressure: {ann_p_with_sbp} ${depthPoints?.ann_p_with_sbp?.label}`,
      columnFields: [
        {
          field: 'ecd_with_sbp',
          headerName: `ECD (${depthPoints?.ecd_with_sbp?.label})`,
          width: 120,
          headerAlign: 'center',
          align: 'center',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return '';
            }
            const value = Number(params.value);
            return isNaN(value) ? params.value : value.toFixed(depthPoints?.ecd_with_sbp?.decimals);
          },
        },
        {
          field: 'ann_p_with_sbp',
          headerName: `Annular Pressure (${depthPoints?.ann_p_with_sbp?.label})`,
          width: 160,
          headerAlign: 'center',
          align: 'center',
        },
      ],
    },
    {
      name: 'PP',
      key: 'pp',
      color: HYDRAULICS_CHART_COLORS.PORE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${window?.units?.md?.label} <br/> PP: {x} ${window?.units?.pp?.label}`
    },
    {
      name: 'FP',
      key: 'fp',
      color: HYDRAULICS_CHART_COLORS.FRACTURE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${window?.units?.md?.label} <br/> FP: {x} ${window?.units?.fp?.label}`
    },
  ]);

  function generateRandom() {
    const length = 24;
    const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let retVal = '';
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }

  const generateWindowDataPoints = (config) => {
    const dataPoints = [];

    if (window?.data.length === 1) {
      const singleDataPoint = window?.data[0];
      dataPoints.push(
        { x: singleDataPoint[config.key], y: 0, markerType: 'none' },
        { x: singleDataPoint[config.key], y: singleDataPoint.md },
        { x: singleDataPoint[config.key], y: chartData.at(-1).md, markerType: 'none' }
      );
    } else if (window?.data.length > 1) {
      const firstPoint = window?.data[0];
      const lastPoint = window?.data.at(-1);

      if (firstPoint.md > 0) {
        dataPoints.push({ x: firstPoint[config.key], y: 0, markerType: 'none' });
      }

      window?.data.forEach(item => {
        dataPoints.push({ x: item[config.key], y: item.md });
      });

      dataPoints.push({ x: lastPoint[config.key], y: chartData.at(-1).md, markerType: 'none' });
    }
    const formattedData = dataPoints.map(item => {
      const formattedItem = {
        x: parseFloat(Number(item.x).toFixed(window?.units?.md?.decimals)),
        y: parseFloat(Number(item.y).toFixed(window?.units?.pp?.decimals)),
      };
      if (item.markerType) {
        formattedItem.markerType = item.markerType;
      }
      return formattedItem;
    });
    return formattedData;
  };

  const generateViewportMinMax = () => {
    const minMaxData = [...chartData, ...window.data];
    const keysToCheck = ['esd', 'ecd', 'esd_with_sbp', 'ecd_with_sbp', 'pp', 'fp'];

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };

    return results;
  };

  const checkEquality = (objects) => {
    return {
      ecdEqual: objects.every(obj => obj.ecd === obj.ecd_with_sbp),
      esdEqual: objects.every(obj => obj.esd === obj.esd_with_sbp),
    };
  };

  useEffect(() => {
    const { ecdEqual, esdEqual } = checkEquality(chartData);
    const hasWindowData = window?.data?.length > 0;

    const updatedChartConfig = chartConfig.filter(item => {
      const removeLegend =
        (item.key === 'ecd_with_sbp' && ecdEqual) ||
        (item.key === 'esd_with_sbp' && esdEqual);
      const shouldRemoveWindowKeys = !hasWindowData && (item.key === 'pp' || item.key === 'fp');
      return !(removeLegend || shouldRemoveWindowKeys);
    });

    const baseColumnFields = [
      {
        field: 'md',
        headerName: `MD (${depthPoints?.md?.label})`,
        headerAlign: 'center',
        align: 'center',
      }
    ];

    const chartConfigData = updatedChartConfig.map(item => ({
      ...item,
      columnFields: [...baseColumnFields, ...(item.columnFields || [])]
    }));

    const filteredData = chartConfigData.filter(item => !['fp', 'pp'].includes(item.key));
    setSelectionMenu(filteredData);

    const charttableItem = filteredData.find(item => item.key === 'ecd_with_sbp') ||
      filteredData.find(item => item.key === 'ecd') ||
      filteredData[0] || '';

    setCharttable(charttableItem);
    setChartConfig(chartConfigData);
  }, []);

  useEffect(() => {
    if (featuredWell?.run_details) {
      const { drillFluids = [] } = hydraulicsLanding?.data || {};
      const drillFluid = drillFluids.find(item => item._id === featuredWell?.run_details.drillFluidsId) || drillFluids[0];
      const runDetails = {
        ...featuredWell?.run_details,
        _id: generateRandom(),
        rop: data?.row?.inputs?.rop,
        flow_in: data?.row?.inputs?.flow_in,
        sbp: data?.row?.sbp,
        rpm: data?.row?.inputs?.rpm ?? 0.0,
        drillStringsId: `Drillstring @ ${featuredWell?.run_details.hole_depth.toFixed(units?.drillStrings?.MD.decimals)}${units?.drillStrings?.MD.label}`,
        drillFluidsId: drillFluid
          ? `${drillFluid.name.trim()} @ ${drillFluid.MD.toFixed(units?.drillFluids?.MD?.decimals)}${units?.drillFluids?.MD?.label}`
          : ''
      };
      setRundetails([runDetails]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (charttable) {
      getTableDatas(charttable);
    }
  }, [charttable]);

  const extractValues = (dataArray, config) => {
    return dataArray.map(item => {
      const extracted = config?.columnFields?.reduce((acc, column) => {
        const field = column.field;
        if (item[field] !== undefined) {
          acc[field] = item[field];
        }
        return acc;
      }, {});
      extracted._id = Math.random().toString(36).substring(2, 10);
      return extracted;
    });
  };

  const getTableDatas = (charttable) => {
    const extractedValues = extractValues(chartData, charttable);
    setTableRecord(extractedValues);
  };

  const CustomLegend = () => {
    const updateHide = (key, hideValue) => {
      setChartConfig(prevItems =>
        prevItems.map(item =>
          item.key === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    return (
      <Grid container justifyContent={isMobile && 'center'}>
        {chartConfig.map((item) => (
          <Box
            key={item.name}
            className={classes.legendItem}
            sx={{ opacity: item.hide ? 0.4 : 1 }}
            onClick={() => {
              updateHide(item.key, !item.hide);
            }}
          >
            <LinearScaleIcon fontSize='large' sx={{ color: item.color, mr: 1 }} />
            {item.name}
          </Box>
        ))}
      </Grid>
    );
  };

  const options = {
    theme: 'light2',
    animationEnabled: !isMobile,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    toolbar: {
      itemBackgroundColor: '#d3d3d3',
      itemBackgroundColorOnHover: '#3e3e3e',
      buttonBorderColor: '#3e3e3e',
    },
    backgroundColor: '#FFF',
    axisX: {
      viewportMinimum: generateViewportMinMax()?.lowest - 0.1,
      viewportMaximum: generateViewportMinMax()?.highest + 0.1,
      interval: getOptimalInterval(generateViewportMinMax()?.lowest, generateViewportMinMax()?.highest, 10),
      valueFormatString: '0.0',
      labelFontSize: isMobile ? 12 : 16,
      gridThickness: 1,
      title: `EMW (${depthPoints?.esd?.label})`,
      titleFontSize: isMobile ? 14 : 22,
    },
    axisY: {
      viewportMinimum: null,
      viewportMaximum: null,
      title: `MD (${depthPoints?.md?.label})`,
      titleFontSize: isMobile ? 14 : 22,
      labelFontSize: isMobile ? 12 : 16,
      includeZero: false,
      valueFormatString: '0',
      reversed: true,
      labelPlacement: 'outside',
      gridThickness: 1,
    },
    height: isMobile ? 450 : 800,
    legend: {
      fontSize: 20
    }
  };

  const WebChartTableComponent = () => {
    return (
      <>
        <Grid container item xs={showTable ? 8 : 12} marginTop={'17px'} alignItems="center" justifyContent="center">
        <Grid item>
          <CustomLegend />
        </Grid>
      </Grid>
      <Box style={{
        overflow: 'auto',
        marginTop: '-1.4%'
      }}>
        <Grid container>
          <Grid item xs={showTable ? 8 : 12} sx={{
            p: 3,
            pt: 4,
            '.canvasjs-chart-toolbar': {
              top: '-20px !important',
            },
          }}>
            <CanvasJSChart
              options={{
                ...options,
                data: chartConfig.map((config) => {
                  if (config.hide) return {};
                  if (config.key === 'pp' || config.key === 'fp') {
                    return {
                      type: 'line',
                      name: config.name,
                      showInLegend: false,
                      toolTipContent: config.tooltip,
                      dataPoints: generateWindowDataPoints(config),
                      color: config.color,
                    };
                  }
                  return {
                    type: 'line',
                    name: config.name,
                    showInLegend: false,
                    toolTipContent: config.tooltip,
                    dataPoints: chartData.map(item => ({
                      x: item[config.key],
                      y: item.md,
                      ann_p: item?.ann_p,
                      ann_p_with_sbp: item?.ann_p_with_sbp,
                    })),
                    color: config.color,
                  };
                }),
              }}
            />
          </Grid>
          {!showTable && <Box className={classes.menuTogglerBtn}>
            <IconButton
              onClick={() => {
                setShowTable(!showTable);
              }}>
              <ChevronLeft sx={{ fontSize: '33px' }} />
            </IconButton>
          </Box>}
          {showTable && <Grid item xs={4} marginTop={'10px'}>
            <Box className={classes.menuTogglerTableBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronRight sx={{ fontSize: '33px' }} />
              </IconButton>
            </Box>
            <Box display={'flex'} justifyContent={'end'}>
              <Grid item xs={3} display="flex" justifyContent="flex-end" marginRight={'1.25%'}>
                <FormControl
                  fullWidth
                  sx={{ bgcolor: 'transparent', minWidth: '100px', p: 0, m: 0 }}
                >
                  <InputLabel id={'filter-input-label'}>Filter</InputLabel>
                  <Select
                    labelId={'filter-select-id'}
                    id={'filter-id'}
                    value={charttable}
                    label='Filter'
                    onChange={(e) => setCharttable(e.target.value)}
                    sx={{
                      color: charttable.color,
                      fontWeight: 'bolder',
                      fontSize: '18px',
                      background: 'unset'
                    }}
                  >
                    {selectionMenu?.map((item, idItem) => (
                      <MenuItem key={`item_${item?.name}_${idItem}`} value={item} sx={{ color: item?.color, fontSize: '18px' }} style={{ fontWeight: 'bold' }}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Box>
            {tableRecord && <Grid item xs={12}>
              <Datagrid
                data={tableRecord}
                sx={{
                  '.MuiDataGrid-cell': {
                    fontSize: '1rem',
                    padding: '2px',
                  },
                  '.MuiDataGrid-columnHeader': {
                    fontSize: '1.2rem',
                    fontWeight: '900 !important',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'visible',
                    lineHeight: '1.43rem',
                    whiteSpace: 'normal',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                }}
                loading={false}
                columns={charttable?.columnFields}
                disableStickyHeader={true}
                autoHeight={false}
                hideFooterPagination
                hideFooterSelectedRowCount
                height={'770px'}
                getRowHeight={() => 'auto'}
                pageSize={100}
                toolbar={{
                  options: {
                    columns: false,
                    filters: false,
                    density: false,
                    export: {
                      enabled: true,
                    }
                  },
                }}
              />
            </Grid>}
          </Grid>}
          <Grid item xs={12} marginBottom={'15px'}>
            <Datagrid
              data={runDetails}
              sx={{
                '.MuiDataGrid-cell': {
                  fontSize: '1rem',
                  fontWeight: 900
                },
                '.MuiDataGrid-columnHeader': {
                  fontSize: '1.2rem',
                  fontWeight: '900 !important'
                },
                '& .MuiDataGrid-footerContainer': {
                  display: 'none',
                },
              }}
              loading={false}
              getRowId={(row) => row._id}
              columns={columns(renderCell, units?.parametricAnalysis?.inputs)}
              onRowClick={(params, evt) => {
                evt.preventDefault();
                evt.stopPropagation();
              }}
              disableStickyHeader={false}
              autoHeight={false}
              autoheight
              height={'150px'}
              hideFooterPagination
              hideFooterSelectedRowCount
              editMode="cell"
              toolbar={{
                options: {
                  columns: false,
                  filters: false,
                  density: false,
                  export: false
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
      </>
    );
  };

  const handleScrollToTop = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-window',
    );

    if (anchor) {
      anchor.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  const MobileChartTableComponent = () => {
    return (
      <Grid sx={{ overflowY: 'auto' }}>
        {!showMobileChartTable
          ? (
            <>
              <Grid>
                <CustomLegend />
                <CanvasJSChart
                  options={{
                    ...options,
                    data: chartConfig.map((config) => {
                      if (config.hide) return {};
                      if (config.key === 'pp' || config.key === 'fp') {
                        return {
                          type: 'line',
                          name: config.name,
                          showInLegend: false,
                          toolTipContent: config.tooltip,
                          dataPoints: generateWindowDataPoints(config),
                          color: config.color,
                        };
                      }
                      return {
                        type: 'line',
                        name: config.name,
                        showInLegend: false,
                        toolTipContent: config.tooltip,
                        dataPoints: chartData.map(item => ({
                          x: item[config.key],
                          y: item.md,
                          ann_p: item?.ann_p,
                          ann_p_with_sbp: item?.ann_p_with_sbp,
                        })),
                        color: config.color,
                      };
                    }),
                  }}
                />
                {runDetails.map((row, index) => (
                  <Box key={index} className={classes.gridContainer}>
                    {columns(renderCell, units?.parametricAnalysis?.inputs)?.map((col, idx) => {
                      const value = col?.valueGetter
                        ? col.valueGetter({ row })
                        : col?.valueFormatter
                          ? col.valueFormatter({
                            row,
                            field: col.field,
                            value: row[col.field],
                          })
                          : row[col.field];
                      return (
                        <Grid container key={idx} className={classes.gridItem}>
                          <Typography className={classes.headerName} variant="body1">
                            {col.headerName}
                          </Typography>
                          <Typography sx={{ flex: 0.5 }} variant="body1">{value}</Typography>
                        </Grid>
                      );
                    })}
                  </Box>
                ))}
              </Grid>
              <Box className={classes.menuTogglerMobileBtn}>
                <IconButton
                  onClick={() => {
                    setShowMobileChartTable(true);
                  }}>
                  <ChevronLeft sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
            </>
            )
          : (
            <Grid item xs={12}>
              <Grid>
                <Box id='back-to-top-window' />

                {tableRecord.map((row, index) => (
                  <Box key={index} className={classes.gridContainer}>
                    {charttable?.columnFields?.map((col, idx) => {
                      const value = col?.valueGetter
                        ? col.valueGetter({ row })
                        : col?.valueFormatter
                          ? col.valueFormatter({
                            row,
                            field: col.field,
                            value: row[col.field],
                          })
                          : row[col.field];
                      return (
                        <Grid container key={idx} className={classes.gridItem}>
                          <Typography className={classes.headerName} variant="body1">
                            {col.headerName}
                          </Typography>
                          <Typography sx={{ flex: 0.5 }} variant="body1">{value}</Typography>
                        </Grid>
                      );
                    })}
                  </Box>
                ))}
              </Grid>
              <Box className={classes.menuTogglerTableMobileBtn}>
                <IconButton
                  onClick={() => {
                    setShowMobileChartTable(false);
                  }}>
                  <ChevronRight sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
              <Fab size='small' className={classes.accordianScrollUp} onClick={handleScrollToTop}>
                <KeyboardArrowUp sx={{ fontSize: '30px' }} />
              </Fab>
            </Grid>
            )}
      </Grid>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseClick}
      maxWidth={'xl'}
      fullScreen={isMobile}
      fullWidth={true}
      style={{
        marginRight: !isMobile && '20px'
      }}
      PaperProps={{
        sx: {
          overflow: 'visible'
        },
      }}
    >
      <Box className={classes.dialogTitle}>
        <Grid container alignItems="center" justifyContent={isMobile && showMobileChartTable ? 'space-between' : 'flex-end'}>
          {isMobile && showMobileChartTable
            ? (
              <Grid item sx={{ mt: '-14px' }}>
                <FormControl
                  fullWidth
                  sx={{
                    bgcolor: 'transparent',
                    width: { xs: '120px', sm: '140px', md: '160px' },
                    height: { xs: '36px', sm: '40px', md: '48px' },
                  }}
                >
                  <InputLabel
                  id={'filter-input-label'}
                  sx={{
                    fontSize: { xs: '12px', sm: '14px', md: '16px' },
                  }}
                  >
                    Filter
                  </InputLabel>
                  <Select
                    labelId={'filter-select-id'}
                    id={'filter-id'}
                    value={charttable}
                    label='Filter'
                    onChange={(e) => setCharttable(e.target.value)}
                    sx={{
                      color: charttable.color,
                      fontWeight: 'bold',
                      fontSize: { xs: '12px', sm: '14px', md: '18px' },
                      py: { xs: 0.2, sm: 0.5, md: 1 },
                      height: { xs: '32px', sm: '40px', md: '48px' },
                      background: 'unset',
                    }}
                  >
                    {selectionMenu?.map((item, idItem) => (
                      <MenuItem
                          key={`item_${item?.name}_${idItem}`}
                          value={item}
                          sx={{
                            fontSize: { xs: '12px', sm: '14px', md: '18px' },
                            fontWeight: 'bold',
                            color: item?.color,
                          }}
                        >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              )
            : null}
          <Grid item>
            <IconButton
              sx={{ mr: !isMobile && 2 }}
              onClick={() => {
                onCloseClick();
              }}
            >
              <CancelPresentationRounded fontSize='large' />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      {isMobile
        ? (
          <MobileChartTableComponent />
          )
        : (
          <WebChartTableComponent />
          )}
    </Dialog>
  );
}

RunDetailsChartDialog.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  data: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  hydraulicsLanding: PropTypes.any,
  rowData: PropTypes.object,
  units: PropTypes.object,
  featuredWell: PropTypes.object,
  window: PropTypes.any
};

export default RunDetailsChartDialog;
